import { makeFetchOrThrow } from '@/data/helpers/makeFetch';
import { Method } from '@/data/enums/method';
import { QueryKey } from '@/data/enums/queryKeys';
import { Services } from '@/data/enums/services';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { transformHouseResponse } from '@/data/house/transformHouseResponse';
import { useAppContext } from '@/providers/AppContextProvider/AppContextProvider';
import { useEffectiveHouseId } from '@/hooks/useEffectiveHouseId/useEffectiveHouseId';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import type { EnabledFeaturesApiRequest, House, HouseResponse } from '@/types/house.types';

type PutEnabledFeaturesContext = {
    previous?: House;
};

export const usePutEnabledFeatures = () => {
    const t = useTranslations('AdminToolbar.crossListing');
    const { deployment, token } = useAppContext();
    const houseId = useEffectiveHouseId();
    const queryClient = useQueryClient();
    const queryKey = [QueryKey.House, houseId];

    return useMutation<HouseResponse, Error, EnabledFeaturesApiRequest, PutEnabledFeaturesContext>({
        mutationFn: (body) => {
            if (!houseId) {
                throw new Error('No house ID found. Please contact your administrator.');
            }
            if (!token) {
                throw new Error('No token found. Please contact your administrator.');
            }

            return makeFetchOrThrow<HouseResponse>({
                apiPath: `houses/${houseId}/enabled-features`,
                apiService: Services.SellerPortal,
                body,
                deployment,
                method: Method.Put,
                token,
            });
        },
        mutationKey: [QueryKey.House, houseId, 'enabled-features'],
        onError: (_error, _variables, context) => {
            if (context?.previous) {
                queryClient.setQueryData(queryKey, context.previous);
            }
            toast('error', {
                description: t('toastError'),
                title: t('toastErrorTitle'),
            });
        },
        onMutate: async ({ enabledFeatures }) => {
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData<House>(queryKey);

            if (previous) {
                queryClient.setQueryData<House>(queryKey, { ...previous, enabledFeatures });
            }

            return { previous };
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey });
        },
        onSuccess: (response) => {
            queryClient.setQueryData(queryKey, transformHouseResponse(response));
        },
    });
};
